'use client';

/**
 * Provider Referral Inbox (TRUS-728)
 *
 * Referrals another organization SENT to the active org (service-referral
 * matches addressed to it): list, review why the member was referred and the
 * SDOH needs on record, then accept or decline. Each referral shows a short
 * checklist for the initiating visit so Community Health Integration (CHI,
 * HCPCS G0019/G0022) services can follow.
 *
 * The provider is always the active org — the backend derives it from the
 * session + X-CBO-ID and ignores any provider id from the client. Member
 * contact details arrive only after the org has accepted.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Inbox,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  User,
  Building2,
  FileText,
  RefreshCw,
  ChevronRight,
  Loader2,
  Filter,
  ClipboardCheck,
  Stethoscope,
  HeartHandshake,
  Lock,
} from 'lucide-react';
import { apiClient } from '@/services/apiClient';
import { useFormattedDate } from '../../hooks/useUserTimezone';

// ============================================================================
// TYPES — mirror app/routers/provider_acceptance.py
// ============================================================================

interface InboxReferral {
  match_id: number;
  referral_id: number;
  referral_number: string | null;
  service_type_code: string | null;
  service_name: string | null;
  urgency: string | null;
  referral_status: string | null;
  response_status: string;
  match_score: number | null;
  notified_at: string | null;
  response_deadline: string | null;
  referral_created: string | null;
  member_name: string | null;
  referring_org_name: string | null;
}

interface InboxCounts {
  total: number;
  pending: number;
  overdue: number;
  accepted: number;
  declined: number;
}

interface SdohNeed {
  label: string;
  domain_code?: string;
  category_code?: string;
  severity?: string;
  is_primary?: boolean;
}

interface ReferralDetail {
  referral: {
    id: number;
    referral_number: string | null;
    service_type_code: string | null;
    service_name: string | null;
    urgency: string | null;
    status: string | null;
    source_type: string | null;
    referral_source_category: string | null;
    service_description: string | null;
    clinical_notes: string | null;
    sdoh_needs: SdohNeed[];
    sdoh_domain_code: string | null;
    sdoh_category_code: string | null;
    sdoh_factors: string[];
    estimated_units: number | null;
    created_at: string | null;
    consent_obtained_at: string | null;
  };
  match: {
    id: number;
    response_status: string;
    match_score: number | null;
    notified_at: string | null;
    response_deadline: string | null;
    responded_at: string | null;
    decline_reason: string | null;
  };
  member: {
    id: number | null;
    first_name: string | null;
    last_name: string | null;
    preferred_language: string | null;
    city: string | null;
    state: string | null;
    email?: string | null;
    phone?: string | null;
  };
  referring_org: { id: number | null; name: string | null };
  contact_released: boolean;
}

interface ProviderAcceptancePortalProps {
  providerName?: string;
  className?: string;
}

const EMPTY_COUNTS: InboxCounts = { total: 0, pending: 0, overdue: 0, accepted: 0, declined: 0 };

/** Data codes (e.g. "food_insecurity") shown as words; they are values, not UI copy. */
const humanize = (code: string) => code.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const isOverdue = (r: { response_status: string; response_deadline: string | null }) =>
  r.response_status === 'pending' && !!r.response_deadline && new Date(r.response_deadline).getTime() < Date.now();

const hoursLeft = (deadline: string | null) =>
  deadline ? Math.round((new Date(deadline).getTime() - Date.now()) / 36e5) : null;

// ============================================================================
// COMPONENT
// ============================================================================

export function ProviderAcceptancePortal({ providerName, className = '' }: ProviderAcceptancePortalProps) {
  const { t } = useTranslation();
  const { fmt } = useFormattedDate();

  const [referrals, setReferrals] = useState<InboxReferral[]>([]);
  const [counts, setCounts] = useState<InboxCounts>(EMPTY_COUNTS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const [urgencyFilter, setUrgencyFilter] = useState<string>('all');

  const [selected, setSelected] = useState<ReferralDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showDetail, setShowDetail] = useState(false);

  const [showAccept, setShowAccept] = useState(false);
  const [showDecline, setShowDecline] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [acceptNotes, setAcceptNotes] = useState('');
  const [declineReason, setDeclineReason] = useState('');
  const [declineReasonCode, setDeclineReasonCode] = useState('');

  const declineReasons = [
    { value: 'no_capacity', label: t('providerInbox.decline.reasons.noCapacity') },
    { value: 'out_of_scope', label: t('providerInbox.decline.reasons.outOfScope') },
    { value: 'geographic', label: t('providerInbox.decline.reasons.geographic') },
    { value: 'schedule_conflict', label: t('providerInbox.decline.reasons.scheduleConflict') },
    { value: 'other', label: t('providerInbox.decline.reasons.other') },
  ];

  // ========================================================================
  // DATA
  // ========================================================================

  const loadInbox = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ status: statusFilter, limit: '50' });
      if (urgencyFilter !== 'all') params.set('urgency', urgencyFilter);
      const response = await apiClient.get(`/api/provider-acceptance/inbox?${params}`);
      setReferrals(response.data?.referrals || []);
      setCounts(response.data?.counts || EMPTY_COUNTS);
    } catch (err) {
      console.error('Failed to load provider inbox:', err);
      setError(t('providerInbox.errors.load'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, urgencyFilter, t]);

  useEffect(() => {
    loadInbox();
  }, [loadInbox]);

  const openReferral = async (matchId: number) => {
    setDetailLoading(true);
    setShowDetail(true);
    setActionError(null);
    try {
      const response = await apiClient.get(`/api/provider-acceptance/referral/${matchId}`);
      setSelected(response.data);
    } catch (err) {
      console.error('Failed to load referral details:', err);
      setShowDetail(false);
      setError(t('providerInbox.errors.detail'));
    } finally {
      setDetailLoading(false);
    }
  };

  const errorDetail = (err: unknown, fallback: string) => {
    const detail = (err as { response?: { status?: number } })?.response?.status;
    return detail === 409 ? t('providerInbox.errors.alreadyAnswered') : fallback;
  };

  const handleAccept = async () => {
    if (!selected) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await apiClient.post(`/api/provider-acceptance/referral/${selected.match.id}/accept`, {
        notes: acceptNotes || undefined,
      });
      setShowAccept(false);
      setAcceptNotes('');
      await openReferral(selected.match.id); // reload: contact details are now released
      loadInbox();
    } catch (err) {
      console.error('Failed to accept referral:', err);
      setActionError(errorDetail(err, t('providerInbox.errors.accept')));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDecline = async () => {
    if (!selected || !declineReason.trim()) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await apiClient.post(`/api/provider-acceptance/referral/${selected.match.id}/decline`, {
        reason: declineReason.trim(),
        reason_code: declineReasonCode || undefined,
      });
      setShowDecline(false);
      setShowDetail(false);
      setDeclineReason('');
      setDeclineReasonCode('');
      loadInbox();
    } catch (err) {
      console.error('Failed to decline referral:', err);
      setActionError(errorDetail(err, t('providerInbox.errors.decline')));
    } finally {
      setActionLoading(false);
    }
  };

  // ========================================================================
  // RENDER HELPERS
  // ========================================================================

  const urgencyBadge = (urgency: string | null) => {
    if (urgency === 'emergent') {
      return <Badge className="bg-red-100 text-red-800 border-red-200">{t('providerInbox.urgency.emergent')}</Badge>;
    }
    if (urgency === 'urgent') {
      return <Badge className="bg-orange-100 text-orange-800 border-orange-200">{t('providerInbox.urgency.urgent')}</Badge>;
    }
    return <Badge className="bg-gray-100 text-gray-700 border-gray-200">{t('providerInbox.urgency.routine')}</Badge>;
  };

  const statusBadge = (status: string, overdue: boolean) => {
    if (overdue) {
      return <Badge className="bg-red-100 text-red-800 border-red-200">{t('providerInbox.status.overdue')}</Badge>;
    }
    if (status === 'pending') {
      return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">{t('providerInbox.status.pending')}</Badge>;
    }
    if (status === 'accepted') {
      return <Badge className="bg-green-100 text-green-800 border-green-200">{t('providerInbox.status.accepted')}</Badge>;
    }
    if (status === 'declined') {
      return <Badge className="bg-gray-100 text-gray-700 border-gray-200">{t('providerInbox.status.declined')}</Badge>;
    }
    return <Badge variant="outline">{humanize(status)}</Badge>;
  };

  const memberName = (d: ReferralDetail) =>
    [d.member.first_name, d.member.last_name].filter(Boolean).join(' ') || t('providerInbox.detail.memberUnnamed');

  const statCards = [
    { key: 'total', value: counts.total, icon: <Inbox className="w-5 h-5 text-gray-400" />, label: t('providerInbox.counts.total') },
    { key: 'pending', value: counts.pending, icon: <Clock className="w-5 h-5 text-yellow-600" />, label: t('providerInbox.counts.pending') },
    { key: 'overdue', value: counts.overdue, icon: <AlertCircle className="w-5 h-5 text-red-600" />, label: t('providerInbox.counts.overdue') },
    { key: 'accepted', value: counts.accepted, icon: <CheckCircle className="w-5 h-5 text-green-600" />, label: t('providerInbox.counts.accepted') },
    { key: 'declined', value: counts.declined, icon: <XCircle className="w-5 h-5 text-gray-400" />, label: t('providerInbox.counts.declined') },
  ];

  /** Why referred + SDOH needs + the CHI initiating-visit checklist. Only data the referral carries. */
  const renderGuidance = (d: ReferralDetail) => {
    const r = d.referral;
    const needs = r.sdoh_needs || [];
    const domainCodes = [r.sdoh_domain_code, r.sdoh_category_code].filter((c): c is string => !!c);
    const factors = r.sdoh_factors || [];
    const hasReason = !!(r.service_description || r.clinical_notes || d.referring_org.name || r.service_name);
    const hasSdoh = needs.length > 0 || domainCodes.length > 0 || factors.length > 0;

    return (
      <div className="p-3 rounded-lg border border-teal-200 bg-teal-50/50 dark:bg-teal-950/20 dark:border-teal-900 space-y-4">
        <h4 className="font-medium flex items-center gap-2">
          <HeartHandshake className="w-4 h-4 text-teal-700" />
          {t('providerInbox.guidance.title')}
        </h4>

        {hasReason && (
          <div className="text-sm space-y-1">
            <p className="font-medium text-gray-700 dark:text-gray-300">{t('providerInbox.guidance.whyReferred')}</p>
            {(d.referring_org.name || r.service_name) && (
              <p className="text-gray-600 dark:text-gray-400">
                {d.referring_org.name && r.service_name
                  ? t('providerInbox.guidance.referredByFor', { org: d.referring_org.name, service: r.service_name })
                  : d.referring_org.name
                    ? t('providerInbox.guidance.referredBy', { org: d.referring_org.name })
                    : t('providerInbox.guidance.referredFor', { service: r.service_name })}
              </p>
            )}
            {r.service_description && <p className="whitespace-pre-wrap">{r.service_description}</p>}
            {r.clinical_notes && (
              <p className="whitespace-pre-wrap text-gray-600 dark:text-gray-400">
                <span className="font-medium">{t('providerInbox.guidance.referrerNotes')}</span> {r.clinical_notes}
              </p>
            )}
          </div>
        )}

        <div className="text-sm space-y-1">
          <p className="font-medium text-gray-700 dark:text-gray-300">{t('providerInbox.guidance.sdohRecorded')}</p>
          {hasSdoh ? (
            <div className="flex flex-wrap gap-2">
              {needs.map((n, i) => (
                <Badge key={`need-${i}`} variant="outline" className="bg-white dark:bg-transparent">
                  {n.label}
                  {n.severity ? ` · ${humanize(n.severity)}` : ''}
                </Badge>
              ))}
              {domainCodes.map((c) => (
                <Badge key={`dom-${c}`} variant="outline" className="bg-white dark:bg-transparent">
                  {humanize(c)}
                </Badge>
              ))}
              {factors.map((f) => (
                <Badge key={`fac-${f}`} variant="outline" className="bg-white dark:bg-transparent">
                  {humanize(f)}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">{t('providerInbox.guidance.sdohNone')}</p>
          )}
        </div>

        <div className="text-sm space-y-2">
          <p className="font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4" />
            {t('providerInbox.guidance.checklistTitle')}
          </p>
          <ul className="space-y-2">
            <li className="flex gap-2">
              <CheckCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${hasSdoh ? 'text-green-600' : 'text-gray-400'}`} />
              <div>
                <p>{t('providerInbox.guidance.checkSdoh')}</p>
                <p className="text-xs text-gray-500">
                  {hasSdoh ? t('providerInbox.guidance.checkSdohHasNeeds') : t('providerInbox.guidance.checkSdohNoNeeds')}
                </p>
              </div>
            </li>
            <li className="flex gap-2">
              <Stethoscope className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" />
              <p>{t('providerInbox.guidance.checkLicensed')}</p>
            </li>
            <li className="flex gap-2">
              <Clock className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" />
              <p>{t('providerInbox.guidance.checkSixMonths')}</p>
            </li>
          </ul>
        </div>
      </div>
    );
  };

  // ========================================================================
  // RENDER
  // ========================================================================

  return (
    <div className={`space-y-6 p-6 ${className}`}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('providerInbox.title')}</h1>
          <p className="text-gray-500 dark:text-gray-400">
            {providerName
              ? t('providerInbox.subtitleWithOrg', { org: providerName })
              : t('providerInbox.subtitle')}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadInbox} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          {t('providerInbox.refresh')}
        </Button>
      </div>

      {/* Counts */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {statCards.map((c) => (
          <Card key={c.key}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                {c.icon}
                <div>
                  <div className="text-2xl font-bold">{c.value}</div>
                  <div className="text-xs text-gray-500">{c.label}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium">{t('providerInbox.filters.label')}</span>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder={t('providerInbox.filters.status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">{t('providerInbox.status.pending')}</SelectItem>
                <SelectItem value="accepted">{t('providerInbox.status.accepted')}</SelectItem>
                <SelectItem value="declined">{t('providerInbox.status.declined')}</SelectItem>
                <SelectItem value="all">{t('providerInbox.filters.allStatuses')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={urgencyFilter} onValueChange={setUrgencyFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder={t('providerInbox.filters.urgency')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('providerInbox.filters.allUrgencies')}</SelectItem>
                <SelectItem value="emergent">{t('providerInbox.urgency.emergent')}</SelectItem>
                <SelectItem value="urgent">{t('providerInbox.urgency.urgent')}</SelectItem>
                <SelectItem value="routine">{t('providerInbox.urgency.routine')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
              <Button variant="outline" size="sm" onClick={loadInbox} className="ml-auto">
                {t('providerInbox.retry')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading && (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      )}

      {!loading && !error && referrals.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <Inbox className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">{t('providerInbox.empty.title')}</h3>
            <p className="text-gray-500">
              {statusFilter === 'pending' ? t('providerInbox.empty.pending') : t('providerInbox.empty.filtered')}
            </p>
          </CardContent>
        </Card>
      )}

      {!loading && !error && referrals.length > 0 && (
        <div className="space-y-3">
          {referrals.map((r) => {
            const overdue = isOverdue(r);
            const hours = hoursLeft(r.response_deadline);
            return (
              <Card
                key={r.match_id}
                className={`hover:shadow-md transition-shadow cursor-pointer ${overdue ? 'border-red-200 bg-red-50/30' : ''}`}
                onClick={() => openReferral(r.match_id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-3 mb-2">
                        {r.referral_number && <span className="font-mono text-sm text-gray-500">{r.referral_number}</span>}
                        {statusBadge(r.response_status, overdue)}
                        {urgencyBadge(r.urgency)}
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                        <span className="flex items-center gap-1 font-medium">
                          <FileText className="w-4 h-4" />
                          {r.service_name || r.service_type_code}
                        </span>
                        {r.member_name && (
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {r.member_name}
                          </span>
                        )}
                        {r.referring_org_name && (
                          <span className="flex items-center gap-1">
                            <Building2 className="w-4 h-4" />
                            {t('providerInbox.list.from', { org: r.referring_org_name })}
                          </span>
                        )}
                      </div>
                      {r.response_status === 'pending' && hours !== null && (
                        <div className="mt-2 text-sm">
                          {hours > 0 ? (
                            <span className="text-amber-600">
                              <Clock className="w-3 h-3 inline mr-1" />
                              {t('providerInbox.list.hoursToRespond', { count: hours })}
                            </span>
                          ) : (
                            <span className="text-red-600 font-medium">
                              <AlertCircle className="w-3 h-3 inline mr-1" />
                              {t('providerInbox.list.overdue')}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 ml-4" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Detail */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {detailLoading || !selected ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex flex-wrap items-center gap-3">
                  {selected.referral.referral_number
                    ? t('providerInbox.detail.titleNumbered', { number: selected.referral.referral_number })
                    : t('providerInbox.detail.title')}
                  {statusBadge(selected.match.response_status, isOverdue(selected.match))}
                  {urgencyBadge(selected.referral.urgency)}
                </DialogTitle>
                <DialogDescription>
                  {t('providerInbox.detail.subtitle', {
                    service: selected.referral.service_name || selected.referral.service_type_code || '',
                    member: memberName(selected),
                  })}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-2">
                {renderGuidance(selected)}

                {/* Member */}
                <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    {t('providerInbox.detail.member')}
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-gray-500">{t('providerInbox.detail.name')}</p>
                      <p className="font-medium">{memberName(selected)}</p>
                    </div>
                    {(selected.member.city || selected.member.state) && (
                      <div>
                        <p className="text-gray-500">{t('providerInbox.detail.location')}</p>
                        <p className="font-medium">
                          {[selected.member.city, selected.member.state].filter(Boolean).join(', ')}
                        </p>
                      </div>
                    )}
                    {selected.member.preferred_language && (
                      <div>
                        <p className="text-gray-500">{t('providerInbox.detail.language')}</p>
                        <p className="font-medium">{selected.member.preferred_language}</p>
                      </div>
                    )}
                    {selected.referral.consent_obtained_at && (
                      <div>
                        <p className="text-gray-500">{t('providerInbox.detail.consentDate')}</p>
                        <p className="font-medium">{fmt.medium(selected.referral.consent_obtained_at)}</p>
                      </div>
                    )}
                    {selected.contact_released && selected.member.phone && (
                      <div>
                        <p className="text-gray-500">{t('providerInbox.detail.phone')}</p>
                        <p className="font-medium">{selected.member.phone}</p>
                      </div>
                    )}
                    {selected.contact_released && selected.member.email && (
                      <div>
                        <p className="text-gray-500">{t('providerInbox.detail.email')}</p>
                        <p className="font-medium break-all">{selected.member.email}</p>
                      </div>
                    )}
                  </div>
                  {!selected.contact_released && (
                    <p className="mt-3 text-xs text-gray-500 flex items-center gap-1">
                      <Lock className="w-3 h-3" />
                      {t('providerInbox.detail.contactAfterAccept')}
                    </p>
                  )}
                </div>

                {selected.match.response_status === 'declined' && selected.match.decline_reason && (
                  <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg text-sm">
                    <p className="text-gray-500">{t('providerInbox.detail.declineReason')}</p>
                    <p>{selected.match.decline_reason}</p>
                  </div>
                )}

                {actionError && (
                  <div className="flex items-center gap-2 text-sm text-red-700">
                    <AlertCircle className="w-4 h-4" />
                    {actionError}
                  </div>
                )}
              </div>

              {selected.match.response_status === 'pending' && (
                <DialogFooter className="flex-col sm:flex-row gap-2">
                  <Button variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => setShowDecline(true)}>
                    <XCircle className="w-4 h-4 mr-1" />
                    {t('providerInbox.actions.decline')}
                  </Button>
                  <Button onClick={() => setShowAccept(true)}>
                    <CheckCircle className="w-4 h-4 mr-1" />
                    {t('providerInbox.actions.accept')}
                  </Button>
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Accept */}
      <Dialog open={showAccept} onOpenChange={setShowAccept}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              {t('providerInbox.accept.title')}
            </DialogTitle>
            <DialogDescription>{t('providerInbox.accept.description')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">{t('providerInbox.accept.notes')}</label>
              <Input
                value={acceptNotes}
                onChange={(e) => setAcceptNotes(e.target.value)}
                placeholder={t('providerInbox.accept.notesPlaceholder')}
                className="mt-1"
              />
            </div>
          </div>
          {actionError && (
            <p className="text-sm text-red-700 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {actionError}
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAccept(false)}>
              {t('providerInbox.actions.cancel')}
            </Button>
            <Button onClick={handleAccept} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              {t('providerInbox.actions.accept')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decline */}
      <Dialog open={showDecline} onOpenChange={setShowDecline}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-600" />
              {t('providerInbox.decline.title')}
            </DialogTitle>
            <DialogDescription>{t('providerInbox.decline.description')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">{t('providerInbox.decline.category')}</label>
              <Select value={declineReasonCode} onValueChange={setDeclineReasonCode}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder={t('providerInbox.decline.categoryPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {declineReasons.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">{t('providerInbox.decline.reason')}</label>
              <Input
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                placeholder={t('providerInbox.decline.reasonPlaceholder')}
                className="mt-1"
                required
              />
            </div>
          </div>
          {actionError && (
            <p className="text-sm text-red-700 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {actionError}
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDecline(false)}>
              {t('providerInbox.actions.cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDecline} disabled={actionLoading || !declineReason.trim()}>
              {actionLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              {t('providerInbox.actions.decline')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ProviderAcceptancePortal;
